<template>
  <div ref="rootEl" class="property-keeper-section">
    <div class="flex justify-space-between bg-u-mb-xl">
      <bg-text size="heading-4">Informasi Pengelola</bg-text>

      <bg-button-icon
        icon="add-plus"
        variant="primary"
        :disabled="keepers.length >= MAX_KEEPERS"
        data-testid="property-keeper-add"
        @click="handleAdd"
      >
        Tambah Informasi Pengelola
      </bg-button-icon>
    </div>

    <bg-card
      v-for="(keeper, i) in keepers"
      :key="i"
      class="bg-u-mb-sm"
      data-testid="property-keeper-item"
    >
      <div class="flex gap-md align-start">
        <bg-field label="Peran Pengelola" class="flex-1">
          <bg-select
            v-model="keeper.role"
            :options="roleOptions"
            placeholder="Pilih peran pengelola"
            :disabled="i === 0"
          />
        </bg-field>

        <validation-provider
          v-slot="{ errors }"
          :rules="i === 0 ? '' : 'required'"
          :custom-messages="{ required: NAME_REQUIRED_MESSAGE }"
          name="Nama Pengelola"
          slim
        >
          <bg-field
            label="Nama Pengelola"
            class="flex-1"
            :error="!!errors.length"
            :message="errors[0]"
          >
            <bg-input
              v-model="keeper.name"
              :disabled="i === 0"
              placeholder="Nama Pengelola"
              data-testid="property-keeper-name"
            />
          </bg-field>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :rules="i === 0 ? '' : { required: true, regex: PHONE_PATTERN }"
          :custom-messages="{
            required: PHONE_REQUIRED_MESSAGE,
            regex: PHONE_FORMAT_MESSAGE,
          }"
          name="No. HP Pengelola"
          slim
        >
          <bg-field
            label="No. HP Pengelola"
            class="flex-1"
            :error="!!errors.length"
            :message="errors[0]"
          >
            <bg-input
              :value="keeper.phone_number"
              :disabled="i === 0"
              inputmode="numeric"
              placeholder="No. HP Pengelola"
              data-testid="property-keeper-phone"
              @input="onPhoneInput(keeper, $event)"
            />
          </bg-field>
        </validation-provider>

        <bg-button-icon
          icon="delete"
          size="lg"
          :disabled="i === 0"
          class="bg-u-mt-xxl"
          data-testid="property-keeper-delete"
          @click="$emit('delete', i, keeper)"
        />
      </div>

      <bg-field label="Catatan">
        <bg-textarea
          v-model="keeper.notification_note"
          :max-char="NOTE_MAX_LENGTH"
          has-counter
          placeholder="Tulis catatan"
          data-testid="property-keeper-note"
        />
      </bg-field>
    </bg-card>
  </div>
</template>

<script>
import { ref, nextTick } from '@vue/composition-api';

import {
  BgText,
  BgCard,
  BgField,
  BgInput,
  BgSelect,
  BgButtonIcon,
  BgTextarea,
} from 'bangul-vue';

import { onlyNumber } from '@/utils/formatter';

const MAX_KEEPERS = 5;
const NOTE_MAX_LENGTH = 100;

const PHONE_PATTERN = /^08\d{8,12}$/;

const NAME_REQUIRED_MESSAGE = 'Masukkan nama pengelola terlebih dahulu.';
const PHONE_REQUIRED_MESSAGE = 'Masukkan nomor HP pengelola terlebih dahulu.';
const PHONE_FORMAT_MESSAGE =
  'Nomor HP harus diawali 08 dan terdiri dari 10-14 digit.';

const KEEPER_ATTRIBUTES = ['role', 'name', 'phone_number', 'notification_note'];

const everyItemHasAttributes = (list, attributes) =>
  list.every(
    item =>
      !!item &&
      typeof item === 'object' &&
      attributes.every(attribute => attribute in item)
  );

export default {
  name: 'PropertyKeeperSection',

  components: {
    BgText,
    BgCard,
    BgField,
    BgInput,
    BgSelect,
    BgButtonIcon,
    BgTextarea,
  },

  props: {
    keepers: {
      type: Array,
      default: () => [],
      validator: keepers => everyItemHasAttributes(keepers, KEEPER_ATTRIBUTES),
    },

    roleOptions: {
      type: Array,
      default: () => [],
      validator: options => everyItemHasAttributes(options, ['val', 'label']),
    },
  },

  setup(_, { emit }) {
    const rootEl = ref(null);

    // The new card renders last; smooth-scroll it into view once it exists.
    const scrollToNewKeeper = () => {
      const items = rootEl.value?.querySelectorAll(
        '[data-testid="property-keeper-item"]'
      );

      items?.[items.length - 1]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    };

    const handleAdd = () => {
      emit('add');
      nextTick(scrollToNewKeeper);
    };

    // Force the phone field to digits only (no number-input spinners).
    const onPhoneInput = (keeper, value) => {
      keeper.phone_number = onlyNumber(value);
    };

    return {
      MAX_KEEPERS,
      NOTE_MAX_LENGTH,
      NAME_REQUIRED_MESSAGE,
      PHONE_REQUIRED_MESSAGE,
      PHONE_PATTERN,
      PHONE_FORMAT_MESSAGE,
      rootEl,
      handleAdd,
      onPhoneInput,
    };
  },
};
</script>
